import React from 'react';
import PropTypes from 'prop-types';
import styled, {css} from 'styled-components';
import {copyFontSizeSmall} from '@nib-components/theme';
import colors from '@nib-components/colors';
import {Box, BoxProps, py, px} from '@nib/layout';

// Prop types
const validTypeValues = ['white', 'light', 'dark', 'success', 'info', 'warning', 'error'] as const;
const validSizeValues = ['small', 'medium'] as const;
type typeValues = (typeof validTypeValues)[number];
type sizeValues = (typeof validSizeValues)[number];

export interface TagProps extends Pick<BoxProps, 'background' | 'color'> {
  type?: typeValues;
  size?: sizeValues;
  children?: React.ReactNode;
  [key: string]: unknown; // other props
}

const pickBackgroundColor = (type: typeValues): string => {
  switch (type) {
    case 'white':
      return colors.white;
    case 'light':
    default:
      return '#e4e4e4';
    case 'dark':
      return ' #636363';
    case 'info':
      return '#005bb9';
    case 'error':
      return '#c81414';
    case 'success':
      return '#007800';
    case 'warning':
      return colors.kate;
  }
};

const pickColor = (type: typeValues): string => {
  switch (type) {
    case 'white':
      return 'inherit';
    case 'light':
    default:
      return 'inherit';
    case 'dark':
      return colors.white;
    case 'info':
      return colors.white;
    case 'error':
      return colors.white;
    case 'success':
      return colors.white;
    case 'warning':
      return 'inherit';
  }
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Wrapper = styled(({type, size, ...rest}) => <Box {...rest} />)<TagProps>`
  display: inline-block;
  white-space: nowrap;

  ${props =>
    props.theme.id === 'nib'
      ? `
        border-radius: 0.25rem;
        font-weight: 500;
      `
      : `
        border-radius: 1rem;
        font-weight: 400;
      `};

  ${({type}) =>
    type &&
    css`
      background-color: ${pickBackgroundColor(type)};
      color: ${pickColor(type)};
    `}

  ${({size}) =>
    size === 'medium'
      ? css`
          font-size: ${copyFontSizeSmall};
          ${py(1)};
          ${props => (props.theme.id === 'nib' ? px(2) : px(3))};
        `
      : css`
          font-size: ${copyFontSizeSmall};
          ${py(0)};
          ${props => (props.theme.id === 'nib' ? px(1) : px(2))};
        `};
`;

const Tag: React.FC<TagProps> = props => {
  const {type, size, children, background, color, ...otherProps} = props;

  return (
    <Wrapper type={type} size={size} background={background} color={color} {...otherProps}>
      {children}
    </Wrapper>
  );
};

Tag.displayName = 'Tag';

Tag.propTypes = {
  type: PropTypes.oneOf(validTypeValues),
  size: PropTypes.oneOf(validSizeValues),
  children: PropTypes.node
};

Tag.defaultProps = {
  size: 'medium'
};

export default Tag;
